import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@iconify/react';
import type { ProcessFolder } from '@/types/process-folder.types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useDeleteProcessFolder } from '@/hooks/process-folder/use-process-folder';

interface DeleteProcessFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder: ProcessFolder | null;
}

export const DeleteProcessFolderModal: React.FC<DeleteProcessFolderModalProps> = ({ isOpen, onClose, folder }) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteProcessFolderMutation = useDeleteProcessFolder();

  const handleDelete = async () => {
    if (!folder) return;

    try {
      setIsDeleting(true);
      await deleteProcessFolderMutation.mutateAsync(folder.id);
      onClose();
    } catch (error: any) {
      console.error('Error deleting process folder:', error);
      
      // Check if it's a validation error about processes in folder
      if (error?.response?.status === 400 && error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t('processManagement.failedToDeleteProcessFolder'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Icon icon="material-symbols:delete-folder" className="text-xl" />
            {t('processManagement.deleteProcessFolder')}
          </DialogTitle>
          <DialogDescription>
            {t('processManagement.deleteProcessFolderDescription', { name: folder?.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-800">
              <Icon icon="material-symbols:warning" className="text-lg" />
              <span className="font-medium">{t('common.warning')}</span>
            </div>
            <p className="text-red-700 text-sm mt-1">
              {folder?.processes && folder.processes.length > 0 
                ? t('processManagement.deleteProcessFolderWithProcessesWarning', { count: folder.processes.length })
                : t('processManagement.deleteProcessFolderWarning')
              }
            </p>
            {folder?.processes && folder.processes.length > 0 && (
              <p className="text-red-600 text-xs mt-2 font-medium">
                {t('processManagement.moveProcessesFirst')}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting || (folder?.processes && folder.processes.length > 0)}
          >
            {isDeleting ? (
              <>
                <Icon icon="eos-icons:loading" className="mr-2" />
                {t('common.deleting')}
              </>
            ) : (
              <>
                <Icon icon="material-symbols:delete" className="mr-2" />
                {t('processManagement.deleteProcessFolder')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

